// Mint-ServiceGP: GP theo dịch vụ / người phụ trách (QĐ 26/Oct). Kỳ ghi nhận dịch vụ không phải cước = ngày hoàn thành dịch vụ.
// GP = REV − (BIL + ABS); OBH, COL không tính GP (COST-LOGIC). Dữ liệu mẫu sinh y hệt script board (seed 7, 64 job). TIỀN → sau quyền `money`.

export type GpSv = 'CCF' | 'COO' | 'FUM' | 'SCY' | 'PTF' | 'DDL' | 'TRF';
export type GpBy = 'svc' | 'pic';
export type GpPer = 'm10' | 'm09' | 'rng';

/** Mã dịch vụ (mã thật trong sysServiceCode) · tên hiển thị. */
export const GP_SERVICES: readonly (readonly [GpSv, string])[] = [
  ['CCF', 'Hải quan'], ['COO', 'C/O'], ['FUM', 'Hun trùng'], ['SCY', 'Bãi · đóng rút'], ['PTF', 'Kiểm dịch'], ['DDL', 'Chuyển phát CT'], ['TRF', 'Vận tải nội địa'],
];
export const GP_PICS = ['HOATTN', 'NHIDTT', 'PHONGNT', 'ANNV', 'THANHNT'] as const;
const PICOF: Record<GpSv, readonly string[]> = { CCF: ['HOATTN', 'NHIDTT'], COO: ['NHIDTT', 'HOATTN'], FUM: ['HOATTN'], SCY: ['PHONGNT', 'ANNV'], PTF: ['NHIDTT'], DDL: ['HOATTN'], TRF: ['THANHNT'] };
const CUS = ['EMDEES', 'NPFruit', 'THANHHAO', 'AGRILINK', 'KHANGTRI', 'TAMNGOC', 'FINEFOOD', 'BLUEBAY', 'THUANPHONG', 'GREENBUD'];
const BASE: Record<GpSv, readonly [number, number]> = { CCF: [1500000, 820000], COO: [850000, 300000], FUM: [1700000, 1150000], SCY: [1600000, 1180000], PTF: [900000, 360000], DDL: [450000, 380000], TRF: [3200000, 2550000] };

export interface GpJob {
  id: string; sv: GpSv; cus: string; pic: string;
  /** Ngày hoàn thành yyyy-mm-dd. */
  done: string;
  rev: number;
  /** BIL + ABS thực tế. */
  cost: number;
  /** BIL + ABS dự kiến. */
  plan: number;
  /** Phát sinh chờ trưởng bộ phận duyệt. */
  wait: number;
  obh: number; abs: number; col: number;
}

function build(): GpJob[] {
  let seed = 7;
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const out: GpJob[] = [];
  for (let i = 0; i < 64; i++) {
    const sv = GP_SERVICES[i % GP_SERVICES.length]![0], b = BASE[sv], pics = PICOF[sv];
    const m = i < 44 ? 10 : 9, d = 1 + Math.floor(rnd() * (m === 10 ? 22 : 30));
    const rev = Math.round((b[0] * (0.8 + rnd() * 0.6)) / 10000) * 10000;
    const plan = Math.round((b[1] * (0.85 + rnd() * 0.3)) / 10000) * 10000;
    const over = rnd() < 0.22 ? 1 + rnd() * 0.35 : 1 - rnd() * 0.04;
    const cost = Math.round((plan * over) / 10000) * 10000;
    const wait = rnd() < 0.18 ? 1 + Math.floor(rnd() * 2) : 0;
    const obh = sv === 'CCF' && rnd() < 0.5 ? Math.round(rnd() * 30 + 2) * 1000000 : rnd() < 0.15 ? Math.round(rnd() * 900 + 100) * 1000 : 0;
    const abs = rnd() < 0.3 ? Math.round((cost * (0.08 + rnd() * 0.12)) / 10000) * 10000 : 0;
    const col = (sv === 'SCY' || sv === 'FUM') && rnd() < 0.3 ? Math.round(rnd() * 4 + 1) * 1000000 : 0;
    out.push({ id: (i % 5 === 0 ? 'SL' : 'SE') + '2606' + String(10000 + i * 137).slice(-5), sv, cus: CUS[(i * 3) % CUS.length]!, pic: pics[i % pics.length]!, done: `2026-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, rev, cost, plan, wait, obh, abs, col });
  }
  return out;
}
export const GP_JOBS: readonly GpJob[] = build();

/** Triệu ₫: 1 số lẻ, dấu phẩy, âm dùng dấu −. */
export const tr = (x: number): string => `${x < 0 ? '−' : ''}${(Math.abs(x) / 1e6).toFixed(1).replace('.', ',')} tr`;
export const pct = (x: number): string => `${x > 0 ? '+' : ''}${x.toFixed(1).replace('.', ',')}%`;
export const pct1 = (x: number): string => `${x.toFixed(1).replace('.', ',')}%`;

export interface GpFilter { per: GpPer; sv: GpSv | 'all'; pic: string; from: string; to: string }
export const DEFAULT_GP_FILTER: GpFilter = { per: 'm10', sv: 'all', pic: 'all', from: '2026-10-01', to: '2026-10-22' };

export function filterJobs(jobs: readonly GpJob[], f: GpFilter): GpJob[] {
  const inPer = (j: GpJob) => (f.per === 'm10' ? j.done.slice(5, 7) === '10' : f.per === 'm09' ? j.done.slice(5, 7) === '09' : j.done >= f.from && j.done <= f.to);
  return jobs.filter((j) => inPer(j) && (f.sv === 'all' || j.sv === f.sv) && (f.pic === 'all' || j.pic === f.pic));
}

export interface GpAgg { n: number; rev: number; cost: number; gp: number; gpP: number; dev: number; devP: number; flag: boolean; wait: number; obh: number; abs: number; col: number; treo: number }
export function aggregate(L: readonly GpJob[]): GpAgg {
  const sum = (f: (j: GpJob) => number) => L.reduce((a, j) => a + f(j), 0);
  const rev = sum((j) => j.rev), cost = sum((j) => j.cost), plan = sum((j) => j.plan);
  const gp = rev - cost, dev = cost - plan, devP = plan ? (dev / plan) * 100 : 0;
  const obh = sum((j) => j.obh), col = sum((j) => j.col);
  return { n: L.length, rev, cost, gp, gpP: rev ? (gp / rev) * 100 : 0, dev, devP, flag: Math.abs(dev) > 500000 && Math.abs(devP) > 5, wait: sum((j) => j.wait), obh, abs: sum((j) => j.abs), col, treo: obh + col };
}

export interface GpRow extends GpAgg { key: string; /** % thanh GP so với dòng cao nhất. */ w: number }
/** Dòng theo dịch vụ (đủ 7, bỏ dòng không có job) hoặc theo người phụ trách; thứ tự như board. */
export function groupRows(L: readonly GpJob[], by: GpBy): GpRow[] {
  const keys: readonly string[] = by === 'svc' ? GP_SERVICES.map((x) => x[0]) : GP_PICS;
  const part = keys.map((k) => ({ key: k, ...aggregate(L.filter((j) => (by === 'svc' ? j.sv : j.pic) === k)) }));
  const mx = Math.max(1, ...part.map((r) => r.gpP));
  return part.filter((r) => r.n > 0).map((r) => ({ ...r, w: Math.max(2, Math.round((Math.max(0, r.gpP) / mx) * 100)) }));
}
